import { createSlice } from '@reduxjs/toolkit';

/**
 * Consultation AI-recorder panel — single-slot persistence.
 *
 * The panel (live transcript / revised transcript / AI propositions / report) is
 * kept in one slot tagged with the appointment id that last STARTED recording.
 * It survives all in-app navigation (tabs, prev/next appointment, remounts) AND a
 * full browser reload (mirrored to localStorage), and is only discarded when
 * recording starts for a different appointment. The audio engine itself
 * (MediaRecorder / AudioContext) is NOT stored here — it stays in useAudioRecorder
 * and is stopped, never persisted, on navigation.
 */

export const RECORDER_STORAGE_KEY = 'doctolik.recorderPanel.v1';

/** A fresh, unshared empty panel (used for initial state and on every claim/reset). */
const emptyPanel = () => ({
  transcriptLines: [], // [{ speaker, text }]
  revisedLines: [], // [{ speaker, text }]
  propositions: { observations: [] }, // { observations: [{ id, text, added }] }
  report: { heading: '', items: [], includeRevised: false }, // items: [{ id, text }]
  isAnalyzing: false,
  error: null,
  panelOpen: false,
  sessionActive: false,
  chunkCount: 0,
});

/** Read the last persisted panel from localStorage (returns null when absent/invalid). */
function loadPersistedRecorder() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(RECORDER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || parsed.appointmentId == null) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Mirror the current slot to localStorage; clears the key once the slot is empty. */
export function persistRecorderState(state) {
  if (typeof window === 'undefined') return;
  try {
    if (!state || state.appointmentId == null) {
      window.localStorage.removeItem(RECORDER_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(RECORDER_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // quota exceeded / private mode — keep working from memory
  }
}

function buildInitialState() {
  const persisted = loadPersistedRecorder();
  if (persisted) {
    return {
      ...emptyPanel(),
      ...persisted,
      // no in-flight request survives a reload
      isAnalyzing: false,
      error: null,
    };
  }
  return { appointmentId: null, ...emptyPanel() };
}

const initialState = buildInitialState();

/** True when the action targets the appointment that currently owns the slot. */
const owns = (state, action) => action.payload?.appointmentId === state.appointmentId;

export const recorderSlice = createSlice({
  name: 'recorder',
  initialState,
  reducers: {
    // Recording started — claim the slot for this appointment. Switching owner
    // discards the previous appointment's panel (single slot). Re-starting on the
    // same appointment keeps its transcript / propositions / report.
    sessionStarted: (state, action) => {
      const appointmentId = action.payload;
      if (appointmentId !== state.appointmentId) {
        Object.assign(state, emptyPanel());
        state.appointmentId = appointmentId;
      }
      state.sessionActive = true;
      state.panelOpen = true;
      state.error = null;
      state.chunkCount = 0;
    },

    panelOpenSet: (state, action) => {
      if (!owns(state, action)) return;
      state.panelOpen = Boolean(action.payload.open);
    },

    analyzingSet: (state, action) => {
      if (!owns(state, action)) return;
      state.isAnalyzing = Boolean(action.payload.value);
    },

    errorSet: (state, action) => {
      if (!owns(state, action)) return;
      state.error = action.payload.message ?? null;
    },

    chunkCountSet: (state, action) => {
      if (!owns(state, action)) return;
      state.chunkCount = action.payload.count;
    },

    transcriptLinesAppended: (state, action) => {
      if (!owns(state, action)) return;
      const { lines } = action.payload;
      if (lines?.length) state.transcriptLines.push(...lines);
    },

    // propositions are merged in the hook (mergePropositions uses an impure id
    // generator); revisedLines are pre-parsed there too.
    analysisApplied: (state, action) => {
      if (!owns(state, action)) return;
      const { propositions, revisedLines } = action.payload;
      if (propositions) state.propositions = propositions;
      if (revisedLines?.length) state.revisedLines.push(...revisedLines);
    },

    // ---- Structured tab -------------------------------------------------------
    propositionItemEdited: (state, action) => {
      if (!owns(state, action)) return;
      const { category, id, text } = action.payload;
      state.propositions[category] = state.propositions[category].map((item) =>
        item.id === id ? { ...item, text } : item,
      );
    },

    propositionItemAdded: (state, action) => {
      if (!owns(state, action)) return;
      const { category, item } = action.payload;
      state.report.items.push({ id: item.id, text: item.text });
      state.propositions[category] = state.propositions[category].map((i) =>
        i.id === item.id ? { ...i, added: true } : i,
      );
    },

    allPropositionsAdded: (state, action) => {
      if (!owns(state, action)) return;
      const { category } = action.payload;
      const toAdd = state.propositions[category].filter((i) => !i.added);
      if (toAdd.length === 0) return;
      state.report.items.push(...toAdd.map((i) => ({ id: i.id, text: i.text })));
      state.propositions[category] = state.propositions[category].map((i) =>
        i.added ? i : { ...i, added: true },
      );
    },

    // ---- Report panel -------------------------------------------------------
    headingEdited: (state, action) => {
      if (!owns(state, action)) return;
      state.report.heading = action.payload.heading;
    },

    reportItemEdited: (state, action) => {
      if (!owns(state, action)) return;
      const { id, text } = action.payload;
      state.report.items = state.report.items.map((item) =>
        item.id === id ? { ...item, text } : item,
      );
    },

    reportItemRemoved: (state, action) => {
      if (!owns(state, action)) return;
      const { id } = action.payload;
      state.report.items = state.report.items.filter((item) => item.id !== id);
      state.propositions.observations = state.propositions.observations.map((item) =>
        item.id === id ? { ...item, added: false } : item,
      );
    },

    // id is generated by the caller (nextManualId, impure) so this stays pure.
    freeTextItemAdded: (state, action) => {
      if (!owns(state, action)) return;
      const { id, text } = action.payload;
      state.report.items.push({ id, text });
    },

    includeRevisedToggled: (state, action) => {
      if (!owns(state, action)) return;
      state.report.includeRevised = !state.report.includeRevised;
    },

    // Manual clear only — not wired to any lifecycle.
    reset: (state) => {
      Object.assign(state, emptyPanel());
      state.appointmentId = null;
    },
  },
});

export const {
  sessionStarted,
  panelOpenSet,
  analyzingSet,
  errorSet,
  chunkCountSet,
  transcriptLinesAppended,
  analysisApplied,
  propositionItemEdited,
  propositionItemAdded,
  allPropositionsAdded,
  headingEdited,
  reportItemEdited,
  reportItemRemoved,
  freeTextItemAdded,
  includeRevisedToggled,
  reset: resetRecorder,
} = recorderSlice.actions;

export const RECORDER_EMPTY_PANEL = Object.freeze(emptyPanel());

// Selectors
export const selectRecorder = (state) => state.recorder;
export const selectRecorderOwnerId = (state) => state.recorder?.appointmentId ?? null;

export default recorderSlice.reducer;
